import type { UploadFile, UploadProps } from "antd"
import { Skeleton } from "antd"
import React, { Suspense, lazy, useMemo, useState } from "react"
import InfiniteScroll from "react-infinite-scroll-component"

import { ProjectFileAddToAlbumModal } from "../../../ProjectFileAddToAlbumModal.tsx"
import { ProjectFileCard } from "../../../ProjectFileCard.tsx"
import { ProjectAllFilesEmpty } from "./ProjectAllFilesEmpty.tsx"
import { ProjectAllFilesUploadCard } from "./ProjectAllFilesUploadCard.tsx"

const FilePreviewModal = lazy(
  () =>
    import("../../../../../../../components/FilePreviewModal/FilePreviewModal")
)

interface ProjectAllFilesProps {
  fileList: UploadFile[]
  currentUserId: string
  uploadAllowed: boolean
  deleteAllowed: boolean
  deleteForeignAllowed: boolean
  editAllowed: boolean
  editForeignAllowed: boolean
  hasMore: boolean
  maxFileSize: number
  allowedExtensions: string[]
  deletingIds: Set<string>
  onLoadMore: () => void
  onBeforeUpload: (file: File) => boolean
  // onCancelUpload: (uid: string) => void
  onDelete: (attachmentId: string) => void
  onRename: (attachmentId: string, nextFileName: string) => Promise<void>
  onAddToAlbum: (albumId: string, fileId: string) => void
  showAddtoAlbum?: boolean
}

export function ProjectAllFiles({
  currentUserId,
  fileList,
  hasMore,
  uploadAllowed,
  deleteAllowed,
  deleteForeignAllowed,
  editAllowed,
  editForeignAllowed,
  maxFileSize,
  allowedExtensions,
  deletingIds,
  onLoadMore,
  onBeforeUpload,
  // onCancelUpload,
  onDelete,
  onRename,
  onAddToAlbum,
  showAddtoAlbum
}: ProjectAllFilesProps) {
  const [previewFile, setPreviewFile] = useState<UploadFile | null>(null)
  const [addToAlbumFileId, setAddToAlbumFileId] = useState<string>()

  const handleAddToAlbum = (albumId: string) => {
    if (!addToAlbumFileId) return
    onAddToAlbum(albumId, addToAlbumFileId)
    setAddToAlbumFileId("")
  }

  const uploadProps: UploadProps = useMemo(
    () => ({
      showUploadList: false,
      multiple: true,
      accept: allowedExtensions.map((ext) => `.${ext}`).join(","),
      beforeUpload: onBeforeUpload
    }),
    [allowedExtensions, onBeforeUpload]
  )

  const hasUploadingFiles = fileList.some((f) => f.status === "uploading")

  if (fileList?.length === 0) {
    return (
      <ProjectAllFilesEmpty
        uploadAllowed={uploadAllowed}
        maxFileSize={maxFileSize}
        uploadProps={uploadProps}
        allowedExtensions={allowedExtensions}
      />
    )
  }

  return (
    <>
      {previewFile?.url && (
        <Suspense
          fallback={
            <Skeleton.Node
              active
              className={"fixed z-10 m-auto inset-0 h-[50vh] w-[50vw]"}
            />
          }
        >
          <FilePreviewModal
            visible
            onClose={() => setPreviewFile(null)}
            doc={{
              file: previewFile?.url,
              file_name: previewFile?.fileName,
              name: previewFile?.name
            }}
          />
        </Suspense>
      )}

      <div
        id="filesScrollableDiv"
        className="overflow-y-auto tailwind-scroll overflow-x-hidden flex-1"
      >
        <InfiniteScroll
          scrollableTarget="filesScrollableDiv"
          dataLength={fileList?.length ?? 0}
          next={onLoadMore}
          hasMore={hasMore}
          loader={
            <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-4 mt-4">
              {Array.from({ length: 7 }).map((_, i) => (
                <Skeleton.Node
                  key={i}
                  active
                  style={{ width: "100%", height: "363px" }}
                  className="w-full"
                />
              ))}
            </div>
          }
        >
          <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-4 min-h-64">
            {uploadAllowed && (
              <ProjectAllFilesUploadCard
                uploadProps={uploadProps}
                allowedExtensions={allowedExtensions}
                hasUploadingFiles={hasUploadingFiles}
                maxFileSize={maxFileSize}
              />
            )}

            {/* File Cards */}
            {fileList.map((file) => (
              <ProjectFileCard
                key={file.uid}
                file={file}
                currentUserId={currentUserId}
                deleteAllowed={deleteAllowed}
                deleteForeignAllowed={deleteForeignAllowed}
                editAllowed={editAllowed}
                editForeignAllowed={editForeignAllowed}
                deleting={deletingIds.has(file.uid)}
                onPreviewFile={setPreviewFile}
                // onCancelUpload={onCancelUpload}
                onDelete={onDelete}
                onRename={onRename}
                onAddToAlbum={(fileId) => setAddToAlbumFileId(fileId)}
                showAddtoAlbum={showAddtoAlbum}
              />
            ))}
          </div>
        </InfiniteScroll>
      </div>
      <ProjectFileAddToAlbumModal
        open={!!addToAlbumFileId}
        onClose={() => setAddToAlbumFileId("")}
        onSave={handleAddToAlbum}
      />
    </>
  )
}
